// src/Pages/Wizard.js — カメラ診断
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createCase, saveCase, submitCase } from "../api/cases";
import { loadProducts, useProducts } from "../api/products";

import { generateSummary } from "../Components/Wizard/generateSummary";
import { useWizardEngine } from "../Components/Wizard/useWizardEngine";
import {
  buildLineSelectionUrl,
  generateCopyText,
  generateSingleProductText,
  selectionTextFrom,
} from "../Components/Wizard/copyText";
import { featureMatches } from "../Components/Wizard/matchers";
import { calcMatches } from "../Components/Wizard/productFilter";
import {
  BackChevron,
  Button,
  Check,
  Chevron,
  Group,
  GroupFooter,
  GroupLabel,
  InfoIcon,
  Row,
  copyToClipboard,
  useToast,
} from "../Components/ui";

import Seo from "../Components/Seo";

// 画面に出す短い見出し（質問の一覧・選んだ条件で使う）
const SHORT_LABELS = {
  mountType: "設置場所",
  buildingType: "建物",
  connectivity: "接続方式",
  recordingMethod: "録画方式",
  lensType: "撮影範囲",
  waterproofDustproof: "防水・防塵",
  resolution: "画質",
  lowLightPerformance: "夜間撮影",
  aiFeatures: "AI機能",
};

// 商品カードに並べる項目（generateSummary のキー）
const SUMMARY_ROWS = [
  ["mountType", "設置場所"],
  ["connectivity", "通信方法"],
  ["recordingMethod", "録画方法"],
  ["lensType", "撮影範囲"],
  ["waterproofDustproof", "防水・防塵"],
  ["resolution", "画質"],
  ["lowLightPerformance", "暗所性能"],
  ["aiFeatures", "AI機能"],
];

const LINE_NOTE = "スマートフォン以外から閲覧されている場合、LINEでのお問い合わせが機能しない場合があります。そのときはメールでご相談ください。";

const optionLabel = (o) => (typeof o === "string" ? o : o.label);
const optionValue = (o) => (typeof o === "string" ? o : o.value);

const getOptionLabel = (question, selectedValue) => {
  if (!selectedValue) return "未選択";
  const matched = question.options.find((o) => optionValue(o) === selectedValue);
  return matched ? optionLabel(matched) : selectedValue;
};

const isNoPreference = (value) => value === "こだわらない";

/** コピーを始めてから、すぐに別の画面（LINE・メール）を開く。ポップアップを止められないように同じ操作の中で開く */
const copyThenOpen = (text, url, { sameWindow = false } = {}) => {
  const copying = copyToClipboard(text);
  if (sameWindow) window.location.href = url;
  else window.open(url, "_blank", "noopener");
  return copying;
};

const productLineMessage = (product) =>
  `商品名: ${product.productName}\n型番: ${product.modelNumber}\nこの商品が気になっているので、詳細について相談したいです。`;

const productLineUrl = (product) =>
  `https://line.me/R/oaMessage/@camarago/?${encodeURIComponent(productLineMessage(product))}`;

const withCaseId = (caseId, text, sep = "\n") => (caseId ? `受付ID : ${caseId}${sep}${text}` : text);

const Wizard = () => {
  // 商品はAPI（データベース）から読み込む。ページを開いた時点で読み込みを始める
  const { products } = useProducts();
  const engine = useWizardEngine({
    products,
    caseApi: { createCase, saveCase, submitCase },
  });
  const { questions, step } = engine;

  // 一度選んだ答えを覚えておく（戻ったときにチェックが残るように）
  const [answers, setAnswers] = useState({});
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    setStartError("");
    try {
      // 商品の読み込みが終わってから質問に進む
      await loadProducts();
      await engine.start();
    } catch (e) {
      console.error(e);
      setStartError("サーバーに接続できませんでした。時間をおいて、もう一度お試しください。");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-fill-2">
      <Seo
        pageTitle="診断ページ"
        description="CAMARAGO(カマラゴ)は、あなたに最適な防犯カメラ・監視カメラを直感的な選択形式で選べるサービスです。設置までもワンストップで提供します。"
        url="https://camarago.com/wizard"
      />
      {step === -1 ? (
        <StartScreen questions={questions} onStart={handleStart} starting={starting} error={startError} />
      ) : step < questions.length ? (
        <QuestionScreen engine={engine} products={products} answers={answers} setAnswers={setAnswers} />
      ) : (
        <ResultScreen engine={engine} />
      )}
    </div>
  );
};

/* ───────── 上部バー ───────── */
function TopBar({ left, center, right }) {
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-fill-2/90 backdrop-blur-xl backdrop-saturate-150">
      <div className="mx-auto grid h-[52px] max-w-6xl grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center px-2 md:px-6">
        <div className="justify-self-start">{left}</div>
        <div className="text-[15px] font-semibold">{center}</div>
        <div className="justify-self-end">{right}</div>
      </div>
    </header>
  );
}

const barButton = "flex min-h-11 items-center gap-0.5 px-2 text-[17px] text-tint hover:text-tint-dark";

const Logo = () => (
  <Link to="/" className="flex min-h-11 items-center px-2" aria-label="CAMARAGO トップへ">
    <img src="/images/camarago_wordmark.png" alt="CAMARAGO" className="h-4 w-auto" />
  </Link>
);

/* ───────── はじめの画面 ───────── */
function StartScreen({ questions, onStart, starting, error }) {
  return (
    <>
      <TopBar left={<Logo />} right={<Link to="/" className={barButton}>閉じる</Link>} />
      <main className="mx-auto flex max-w-xl flex-col px-4 pb-16 pt-10 md:pt-16">
        <h1 className="px-1 text-[30px] font-bold leading-tight tracking-tight md:text-[40px]">
          {questions.length}つの質問で、
          <br />
          ぴったりの防犯カメラを。
        </h1>
        <p className="mt-4 px-1 text-[16px] leading-[1.8] text-ink-2 md:text-[17px]">
          カメラ選びでよくある「多すぎて決められない」という不安を、直感で進める選択肢形式でやさしく解決。使い方や重視したいことを選んでいくだけで、自分に合ったモデルが見つかります。
        </p>

        <div className="mt-8">
          <GroupLabel>おたずねすること</GroupLabel>
          <Group>
            {questions.map((q, i) => (
              <Row key={q.key} detail={<span className="text-[13px]">{i + 1}</span>}>
                <span className="text-[16px]">{q.question}</span>
              </Row>
            ))}
          </Group>
          <GroupFooter>登録や個人情報の入力は必要ありません。1分ほどで終わります。</GroupFooter>
        </div>

        <div className="sticky bottom-0 -mx-4 mt-8 bg-fill-2/90 px-4 pb-6 pt-3 backdrop-blur-xl md:static md:mx-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
          {error && (
            <p role="alert" className="mb-3 rounded-xl bg-white px-4 py-3 text-[15px] text-[#c1272d]">
              {error}
            </p>
          )}
          <Button onClick={onStart} disabled={starting} className="w-full !rounded-[14px] md:w-auto md:!rounded-full md:!px-10">
            {starting ? "準備しています…" : "診断をはじめる"}
          </Button>
        </div>
      </main>
    </>
  );
}

/* ───────── 質問の画面 ───────── */
function QuestionScreen({ engine, products, answers, setAnswers }) {
  const navigate = useNavigate();
  const { questions, step, selectedOptions, select, back, jumpTo } = engine;
  const q = questions[step];
  const pending = answers[q.key];
  const total = questions.length;

  const [aboutOpen, setAboutOpen] = useState(false);
  useEffect(() => {
    const wide = typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches;
    setAboutOpen(Boolean(wide));
  }, [step]);

  // ここまでの条件（いま選んでいる答えも含む）に合う機種の数
  const { liveCount, nearCount } = useMemo(() => {
    const selections = pending ? { ...selectedOptions, [q.key]: pending } : selectedOptions;
    const { exactMatches, alternativeMatches } = calcMatches({ products, selections, featureMatches });
    return { liveCount: exactMatches.length, nearCount: alternativeMatches.length };
  }, [products, selectedOptions, q.key, pending]);

  const choose = (value) => setAnswers((prev) => ({ ...prev, [q.key]: value }));
  const next = () => {
    if (pending) select(q.key, pending);
  };
  const isLast = step === total - 1;
  const progress = ((step + (pending ? 1 : 0)) / total) * 100;

  // ぴったり合う機種が0でも、近いカメラを提案できることを伝える
  const countText =
    liveCount > 0 ? (
      <>
        ここまでの条件に合うカメラ<span className="ml-2 font-semibold text-ink">{liveCount}機種</span>
      </>
    ) : nearCount > 0 ? (
      <>
        ぴったりの機種はなし。<span className="font-semibold text-ink">近いカメラ</span>をご提案できます
      </>
    ) : (
      <>ぴったりの機種はなし。結果画面からご相談いただけます</>
    );

  return (
    <>
      <TopBar
        left={
          step > 0 ? (
            <button type="button" onClick={back} className={barButton}>
              <BackChevron className="h-5 w-3" />
              <span>戻る</span>
            </button>
          ) : (
            <span className="hidden lg:block"><Logo /></span>
          )
        }
        center={
          <span aria-label={`質問 ${step + 1} / ${total}`}>
            {step + 1} / {total}
          </span>
        }
        right={
          <button type="button" onClick={() => navigate("/")} className={barButton}>
            やめる
          </button>
        }
      />
      <div className="h-[3px] bg-black/[0.08]" aria-hidden="true">
        <div className="h-[3px] bg-tint transition-[width] duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="mx-auto grid max-w-6xl gap-14 px-4 pb-40 pt-7 md:px-8 lg:grid-cols-[240px_minmax(0,680px)] lg:pb-20 lg:pt-14">
        {/* パソコン：質問の一覧 */}
        <nav aria-label="質問の一覧" className="hidden flex-col gap-0.5 text-[14px] lg:flex">
          {questions.map((item, i) => {
            const done = i < step;
            const current = i === step;
            const content = (
              <>
                <span>{SHORT_LABELS[item.key]}</span>
                {done && <span className="truncate pl-3 text-right">{getOptionLabel(item, selectedOptions[item.key])}</span>}
                {current && <span className="text-tint">回答中</span>}
              </>
            );
            if (done)
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => jumpTo(i)}
                  className="flex justify-between rounded-lg px-3 py-2 text-left text-ink-2 transition-colors hover:bg-white/70"
                  title="この質問に戻る"
                >
                  {content}
                </button>
              );
            return (
              <span
                key={item.key}
                aria-current={current ? "step" : undefined}
                className={`flex justify-between rounded-lg px-3 py-2 ${current ? "bg-white font-semibold" : "text-ink-3"}`}
              >
                {content}
              </span>
            );
          })}
        </nav>

        <main className="flex flex-col gap-6">
          <div className="px-1">
            <span className="hidden text-[14px] text-ink-2 lg:block">
              質問 {step + 1} / {total}
            </span>
            <h1 className="text-[28px] font-bold leading-tight tracking-tight md:text-[34px] lg:mt-1.5 lg:text-[40px]">
              {q.question}
            </h1>
            <p className="mt-2 text-[15px] text-ink-2 md:text-[17px]">{q.subtitle}</p>
          </div>

          <div role="radiogroup" aria-label={q.question} className="overflow-hidden rounded-[14px] bg-white">
            {q.options.map((o, i) => {
              const value = optionValue(o);
              const selected = pending === value;
              return (
                <button
                  key={`${q.key}-${value}`}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choose(value)}
                  className={`relative flex min-h-[54px] w-full items-center justify-between gap-3 px-4 py-3 text-left text-[17px] transition-colors hover:bg-black/[0.03] active:bg-black/[0.06] lg:min-h-14 lg:px-5 ${
                    selected ? "font-medium" : ""
                  }`}
                >
                  <span>{optionLabel(o)}</span>
                  {selected && <Check className="shrink-0 text-tint" />}
                  {i < q.options.length - 1 && (
                    <span aria-hidden="true" className="pointer-events-none absolute bottom-0 left-4 right-0 h-px bg-line lg:left-5" />
                  )}
                </button>
              );
            })}
          </div>

          {(q.description || q.uncertain) && (
            <div className={aboutOpen ? "rounded-[14px] bg-white px-4 pb-4 lg:px-5" : ""}>
              <button
                type="button"
                aria-expanded={aboutOpen}
                onClick={() => setAboutOpen((v) => !v)}
                className="flex min-h-11 items-center gap-1.5 px-1 text-[15px] text-tint hover:text-tint-dark"
              >
                <InfoIcon />
                <span>この質問について</span>
                <Chevron className={`ml-0.5 h-2.5 w-2 transition-transform ${aboutOpen ? "rotate-90" : ""}`} />
              </button>
              {aboutOpen && (
                <div className="space-y-4 px-1 pt-1 text-[15px] leading-[1.85] text-body">
                  {q.description && <p>{q.description}</p>}
                  {q.uncertain && (
                    <div>
                      <div className="font-semibold text-ink">迷ったときは</div>
                      <p className="mt-0.5">{q.uncertain}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* パソコン：前の質問・次へ */}
          <div className="hidden items-center justify-between pt-2 lg:flex">
            {step > 0 ? (
              <button type="button" onClick={back} className="flex min-h-11 items-center gap-1 text-[17px] text-tint hover:text-tint-dark">
                <BackChevron className="h-4 w-2.5" />
                前の質問
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-5">
              <span className="text-[14px] text-ink-2">{countText}</span>
              <Button onClick={next} disabled={!pending} className="!px-8">
                {isLast ? "結果を見る" : "次へ"}
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* スマホ：下に固定 */}
      <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-fill-2/90 px-4 pb-[max(20px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl backdrop-saturate-150 lg:hidden">
        <div className="mx-auto max-w-xl">
          <div className="mb-2.5 text-center text-[13px] text-ink-2" aria-live="polite">
            {countText}
          </div>
          <Button onClick={next} disabled={!pending} className="w-full !rounded-[14px]">
            {isLast ? "結果を見る" : "次へ"}
          </Button>
        </div>
      </footer>
    </>
  );
}

/* ───────── 結果の画面 ───────── */
function ResultScreen({ engine }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { questions, selectedOptions, filteredProducts, alternativeProducts, hasExact, hasAlternative, caseId, back, reset } =
    engine;
  const [showAll, setShowAll] = useState(false);

  const currentList = hasExact ? filteredProducts : alternativeProducts;
  const selectionText = selectionTextFrom({ questions, selectedOptions });
  const lineSelectionUrl = buildLineSelectionUrl({ selectionText });

  const copy = async (text, message) => {
    const ok = await copyToClipboard(text);
    toast(ok ? message : "コピーできませんでした。ブラウザの設定をご確認ください。");
  };

  const productText = (product) =>
    generateSingleProductText({ product, questions, selections: selectedOptions });

  const openProductMail = (product) => {
    const subject = `【CAMARAGO】商品相談：${product.productName}（${product.modelNumber}）`;
    const body = `以下をご入力のうえ、そのまま送信してください。

【お客様情報】
会社名：
お名前：
電話番号：
メールアドレス：

【ご相談内容】
この商品について詳しく相談したいです。

------------------------------

${withCaseId(caseId, productText(product))}

------------------------------

CAMARAGOからの商品相談メールです。`;
    copyThenOpen(
      body,
      `mailto:contact@camarago.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      { sameWindow: true }
    );
  };

  const openNoMatchMail = () => {
    const subject = "【CAMARAGO】防犯カメラ選定の相談";
    const body = `以下をご入力のうえ、そのまま送信してください。

【お客様情報】
会社名：
お名前：
電話番号：
メールアドレス：

【ご相談内容】
条件に合うカメラが見つからなかったため、最適なカメラについて相談したいです。

------------------------------

受付ID：${caseId || "未発行"}

【選択内容】
${selectionText}

------------------------------

CAMARAGOからの商品相談メールです。`;
    copyThenOpen(
      body,
      `mailto:contact@camarago.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      { sameWindow: true }
    );
  };

  const restart = () => {
    reset();
    window.scrollTo(0, 0);
  };

  // 「こだわらない」以外を先に並べる
  const conditionRows = questions
    .map((q) => ({ q, value: selectedOptions[q.key] }))
    .sort((a, b) => Number(isNoPreference(a.value)) - Number(isNoPreference(b.value)));
  const FIRST = 3;
  const visibleConditions = showAll ? conditionRows : conditionRows.slice(0, FIRST);
  const hiddenCount = conditionRows.length - FIRST;

  const count = currentList.length;

  return (
    <>
      <TopBar
        left={
          <button type="button" onClick={back} className={barButton}>
            <BackChevron className="h-5 w-3" />
            <span>条件を変える</span>
          </button>
        }
        center="診断結果"
        right={
          <button type="button" onClick={() => navigate("/")} className={barButton}>
            完了
          </button>
        }
      />

      <main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-20 pt-6 md:px-8 md:pt-12">
        {/* 見出し */}
        <div className="max-w-2xl px-1">
          {hasExact ? (
            <>
              <h1 className="text-[30px] font-bold leading-tight tracking-tight md:text-[40px]">
                条件に合うカメラが
                <br className="md:hidden" />
                {count}機種あります
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-2 md:text-[17px]">
                気になる商品は、そのまま相談できます。商品情報をコピーしておくと、お問い合わせのときに便利です。
              </p>
            </>
          ) : hasAlternative ? (
            <>
              <h1 className="text-[30px] font-bold leading-tight tracking-tight md:text-[40px]">
                条件に近いカメラが
                <br className="md:hidden" />
                {count}機種あります
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-2 md:text-[17px]">
                すべての条件に合う機種は見つかりませんでしたが、近い候補をご紹介します。条件と異なる項目には印がついています。
              </p>
            </>
          ) : (
            <>
              <h1 className="text-[30px] font-bold leading-tight tracking-tight md:text-[40px]">
                条件に合うカメラが
                <br className="md:hidden" />
                見つかりませんでした
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-2 md:text-[17px]">
                選んだ条件をもとにご相談いただければ、スタッフが最適なご提案をいたします。
              </p>
            </>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start lg:gap-8">
          {/* 選んだ条件 */}
          <aside className="flex flex-col gap-6 lg:sticky lg:top-20">
            <div>
              <GroupLabel>選んだ条件</GroupLabel>
              <Group>
                {visibleConditions.map(({ q, value }) => (
                  <Row key={q.key} detail={<span className="text-[15px]">{getOptionLabel(q, value)}</span>} className="!min-h-11 !py-2.5">
                    <span className="text-[15px]">{SHORT_LABELS[q.key]}</span>
                  </Row>
                ))}
                {hiddenCount > 0 && (
                  <Row
                    onClick={() => setShowAll((v) => !v)}
                    className="!min-h-11 !py-2.5"
                    accessory={<Chevron className={`shrink-0 text-chevron transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />}
                  >
                    <span className="text-[15px] text-tint">{showAll ? "閉じる" : `ほか${hiddenCount}つの条件`}</span>
                  </Row>
                )}
              </Group>
            </div>

            {!hasExact && !hasAlternative ? (
              <div>
                <Group>
                  <Row
                    tint
                    accessory="chevron"
                    onClick={() =>
                      copyThenOpen(`受付ID : ${caseId}　${selectionText}`, lineSelectionUrl)
                    }
                    sub="選んだ条件をコピーして、LINEを開きます"
                  >
                    LINEで相談する
                  </Row>
                  <Row tint accessory="chevron" onClick={openNoMatchMail} sub="選んだ条件が入ったメールを作ります">
                    メールで相談する
                  </Row>
                </Group>
                <GroupFooter>{LINE_NOTE}</GroupFooter>
              </div>
            ) : (
              <div className="hidden lg:block">
                <ResultActions
                  count={count}
                  onCopyAll={() =>
                    copy(
                      withCaseId(caseId, generateCopyText({ products: currentList, questions, selections: selectedOptions })),
                      `${count}機種の情報をコピーしました`
                    )
                  }
                  onCopySelection={() => copy(withCaseId(caseId, selectionTextOneLine(questions, selectedOptions)), "選んだ条件をコピーしました")}
                  onLineSelection={() => copyThenOpen(withCaseId(caseId, selectionText), lineSelectionUrl)}
                  onRestart={restart}
                />
              </div>
            )}
          </aside>

          {/* 商品 */}
          {count > 0 && (
            <section aria-label="商品の一覧" className="flex flex-col gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                {currentList.map((product) => (
                  <ProductCard
                    key={`${product.makerName}-${product.modelNumber}`}
                    product={product}
                    selections={selectedOptions}
                    showMismatch={!hasExact}
                    onLine={() => copyThenOpen(productLineMessage(product), productLineUrl(product))}
                    onMail={() => openProductMail(product)}
                    onCopy={() => copy(withCaseId(caseId, productText(product)), "この商品の情報をコピーしました")}
                  />
                ))}
              </div>
              <p className="px-1 text-[13px] leading-relaxed text-ink-2">{LINE_NOTE}</p>

              <div className="lg:hidden">
                <ResultActions
                  count={count}
                  onCopyAll={() =>
                    copy(
                      withCaseId(caseId, generateCopyText({ products: currentList, questions, selections: selectedOptions })),
                      `${count}機種の情報をコピーしました`
                    )
                  }
                  onCopySelection={() => copy(withCaseId(caseId, selectionTextOneLine(questions, selectedOptions)), "選んだ条件をコピーしました")}
                  onLineSelection={() => copyThenOpen(withCaseId(caseId, selectionText), lineSelectionUrl)}
                  onRestart={restart}
                />
              </div>
            </section>
          )}

          {count === 0 && (
            <div>
              <Group>
                <Row tint onClick={restart}>
                  条件を変えてもう一度診断する
                </Row>
              </Group>
            </div>
          )}
        </div>

        {caseId && <p className="px-1 text-[12px] text-ink-3">受付ID：{caseId}</p>}
      </main>
    </>
  );
}

const selectionTextOneLine = (questions, selectedOptions) =>
  questions.map((q) => `${q.question}：${getOptionLabel(q, selectedOptions[q.key])}`).join(" / ");

function ResultActions({ count, onCopyAll, onCopySelection, onLineSelection, onRestart }) {
  return (
    <Group>
      <Row tint onClick={onCopyAll}>
        {count}機種の情報をまとめてコピー
      </Row>
      <Row tint onClick={onCopySelection}>
        選んだ条件をコピー
      </Row>
      <Row tint onClick={onLineSelection} accessory="chevron">
        選んだ条件でLINE相談する
      </Row>
      <Row tint onClick={onRestart}>
        条件を変えてもう一度診断する
      </Row>
    </Group>
  );
}

function ProductCard({ product, selections, showMismatch, onLine, onMail, onCopy }) {
  const summary = generateSummary(product);

  const mismatch = (key) => {
    if (!showMismatch) return false;
    const selected = selections[key];
    if (!selected || isNoPreference(selected)) return false;
    return !featureMatches(product.features?.[key], selected, key, product);
  };

  return (
    <article className="flex flex-col gap-4 rounded-[18px] bg-white p-4 pt-[18px] md:p-5">
      <div>
        <div className="text-[13px] text-ink-2">
          {product.makerName}
          <span className="ml-3">{product.modelNumber}</span>
        </div>
        <h2 className="mt-0.5 text-[18px] font-bold leading-snug">{product.productName}</h2>
      </div>

      <dl className="rounded-xl bg-fill px-3 text-[14px]">
        {SUMMARY_ROWS.map(([key, label], i) => {
          const value = summary[key];
          const off = mismatch(key);
          return (
            <div
              key={key}
              className={`flex justify-between gap-4 py-2.5 ${i < SUMMARY_ROWS.length - 1 ? "border-b border-line" : ""}`}
            >
              <dt className="shrink-0 text-ink-2">{label}</dt>
              <dd className={`text-right ${off ? "text-ink-2" : ""}`}>
                {Array.isArray(value) ? value.join("、") : value}
                {off && <span className="mt-0.5 block text-[12px] text-[#b25000]">条件と異なる</span>}
              </dd>
            </div>
          );
        })}
      </dl>

      <div className="mt-auto flex flex-col gap-1">
        <Button onClick={onLine} className="w-full !min-h-12 !rounded-xl !text-[16px]">
          LINEで相談する
        </Button>
        <div className="flex justify-center gap-7 text-[15px]">
          <button type="button" onClick={onMail} className="flex min-h-11 items-center text-tint hover:text-tint-dark">
            メールで相談
          </button>
          <button type="button" onClick={onCopy} className="flex min-h-11 items-center text-tint hover:text-tint-dark">
            情報をコピー
          </button>
        </div>
      </div>
    </article>
  );
}

export default Wizard;
